import { html } from 'lit';
import { JVElement } from '../../lib/base';
import { authService } from '../../services/auth-service';
import { createApiClient, ApiError } from '../../services/http-client';
const RESOURCE_ROUTES = {
    '04-tenant-detail': 'tenant-detail', '05-onboard': 'onboard', '06-plans': 'plans',
    '07-relay': 'relay', '08-agents': 'agents', '09-analytics': 'analytics', '10-audit': 'audit',
    '11-staff': 'staff', '12-vendors': 'vendors', '13-recording': 'records',
};
const runtimeConfig = window;
const viteEnv = import.meta.env;
const API_URL = runtimeConfig.JUSTVUE_API_URL || viteEnv?.VITE_API_URL || '';
/**
 * Operator portal app shell. Handles client-side hash routing, auth check,
 * role enforcement (Operator only), and renders the appropriate page component.
 *
 * Routes:
 *   #01-login       → <jv-operator-login>
 *   #02-dashboard   → <jv-operator-dashboard>  (default/home)
 *   #03-tenants     → <jv-operator-tenants>
 *   (future routes stubbed with placeholder)
 */
export class JvOperatorApp extends JVElement {
    static { this.properties = {
        _route: { state: true },
        _user: { state: true },
        _loading: { state: true },
        _error: { state: true },
    }; }
    constructor() {
        super();
        this._onHashChange = () => {
            this._resolveRoute();
        };
        this._onLoginSuccess = () => {
            this._checkAuth();
        };
        this._route = '';
        this._user = null;
        this._loading = true;
        this._error = null;
        this.api = createApiClient({
            baseUrl: API_URL || window.location.origin,
            getToken: () => authService.getToken(),
            onUnauthorized: () => this._logout(),
        });
    }
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('hashchange', this._onHashChange);
        this._init();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('hashchange', this._onHashChange);
    }
    async _init() {
        this._resolveRoute();
        // If already on the login screen, skip auth check
        if (this._route === '01-login') {
            this._loading = false;
            return;
        }
        await this._checkAuth();
    }
    async _checkAuth() {
        this._loading = true;
        this._error = null;
        if (!authService.isAuthenticated()) {
            this._navigate('01-login');
            this._loading = false;
            return;
        }
        try {
            const user = await this.api.get('/auth/me');
            // Operators are always cross-tenant — their role is "Operator"
            const isOperator = user.roles.some((role) => role.toLowerCase() === 'operator');
            if (!isOperator) {
                this._error = 'Access denied: Operator role required.';
                authService.logout();
                this._navigate('01-login');
                this._loading = false;
                return;
            }
            this._user = user;
            // If route is empty or login, go to dashboard
            if (!this._route || this._route === '01-login') {
                this._navigate('02-dashboard');
            }
        }
        catch (e) {
            if (e instanceof ApiError && e.status === 401) {
                authService.logout();
                this._navigate('01-login');
            }
            else {
                this._error = e instanceof Error ? e.message : 'Authentication failed';
            }
        }
        finally {
            this._loading = false;
        }
    }
    _resolveRoute() {
        const hash = window.location.hash.replace(/^#/, '') || '02-dashboard';
        const validRoutes = [
            '01-login', '02-dashboard', '03-tenants', '04-tenant-detail',
            '05-onboard', '06-plans', '07-relay', '08-agents', '09-analytics',
            '10-audit', '11-staff', '12-vendors', '13-recording',
        ];
        const route = hash.split('/')[0];
        this._route = validRoutes.includes(route) ? hash : '02-dashboard';
    }
    _navigate(route) {
        window.location.hash = `#${route}`;
    }
    _logout() {
        authService.logout();
        this._user = null;
        this._navigate('01-login');
    }
    render() {
        if (this._loading) {
            return html `<div class="min-h-screen grid place-items-center bg-jv-bg">
        <div class="flex flex-col items-center gap-3">
          <span class="w-10 h-10 rounded-full border-2 border-jv-primary border-t-transparent animate-spin"></span>
          <p class="text-sm text-jv-ink-2">Loading…</p>
        </div>
      </div>`;
        }
        if (this._error && this._route !== '01-login') {
            return html `<div class="min-h-screen grid place-items-center bg-jv-bg">
        <div class="bg-jv-surface border border-jv-border rounded-2xl p-8 shadow-jv max-w-sm text-center">
          <span class="grid place-items-center w-12 h-12 rounded-xl bg-jv-danger-050 text-jv-danger mx-auto mb-4">
            <span class="jvi-warning text-[24px]"></span>
          </span>
          <h2 class="font-bold text-jv-ink text-lg">Session Error</h2>
          <p class="text-sm text-jv-ink-2 mt-2">${this._error}</p>
          <a href="#01-login" class="inline-flex mt-4 px-4 py-2 rounded-xl bg-jv-primary text-white text-sm font-semibold">
            Sign in again
          </a>
        </div>
      </div>`;
        }
        // --- Login screen (no sidebar, no auth required) ---
        if (this._route === '01-login') {
            return html `<jv-operator-login
        @login-success=${this._onLoginSuccess}
      ></jv-operator-login>`;
        }
        // --- Authenticated screens with sidebar ---
        return html `<div class="flex min-h-screen bg-jv-bg">
      <jv-sidebar
        surface="operator"
        active="${this._route}"
        .displayName=${this._user?.displayName || ''}
      ></jv-sidebar>
      <main class="flex-1 min-w-0">
        ${this._renderPage()}
      </main>
    </div>`;
    }
    _renderPage() {
        switch (this._route) {
            case '02-dashboard':
                return html `<jv-operator-dashboard .api=${this.api}></jv-operator-dashboard>`;
            case '03-tenants':
                return html `<jv-operator-tenants .api=${this.api}></jv-operator-tenants>`;
            default: {
                const route = this._route.split('/')[0];
                const kind = RESOURCE_ROUTES[route];
                const tenantId = this._route.split('/')[1] || '';
                return kind ? html `<jv-operator-resource .api=${this.api} kind="${kind}" tenantId="${tenantId}"></jv-operator-resource>` : html `<div class="max-w-content mx-auto px-5 sm:px-8 py-7"><jv-page-head title="Not found" subtitle="Choose a screen from the navigation."></jv-page-head></div>`;
            }
        }
    }
}
